import { BadgeCheck, MoreHorizontal } from 'lucide-react';
import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import {
  EMPLOYMENT_TYPE_LABELS,
  EMPLOYMENT_TYPES,
  TEMPLATE_STATUS_LABELS,
  WORK_MODE_LABELS,
  WORK_MODES,
  legacyTypeFromKind,
  type EmploymentType,
  type RoleTemplateView,
  type WorkMode,
} from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Checkbox } from '../../../../components/Checkbox';
import { Chip } from '../../../../components/Chip';
import { Field } from '../../../../components/Field';
import { Input, Textarea } from '../../../../components/Input';
import { MenuItem, Popover, usePopover } from '../../../../components/Popover';
import { MoreOptions } from '../../../../components/MoreOptions';
import { Segmented } from '../../../../components/Segmented';
import { Select } from '../../../../components/Select';
import { TEMPLATE_STATUS_TONES } from '../../../../components/status-tones';
import { Tabs, type TabItem } from '../../../../components/Tabs';
import { ToneChip } from '../../../../components/Tone';
import { ApiError, errorMessage } from '../../../../lib/api-client';
import { useModule } from '../../../../lib/modules';
import { useToasts } from '../../../../stores/toasts';
import { useDepartments, useVentures } from '../../../organisation/api/organisation-api';
import { IcpInlineForm } from '../../../icp/components/IcpInlineForm';
import { itemInputOf, itemOfInput } from '../../../icp/icp-form';
import { useCertifyRoleTemplate, usePipelineTemplates, useSaveRoleTemplate } from '../../api/jobs-api';
import { CriteriaTable } from '../builder/CriteriaTable';
import { MatrixTable } from '../builder/MatrixTable';
import { FormBuilder } from '../FormBuilder';
import { StageBuilder } from '../StageBuilder';
import { useLeaveGuard } from './LeaveGuard';
import { RecruiterView } from './RecruiterView';
import { isSameEditableTemplate, isSameTemplateDraft, templateBodyOf, templateDraftOf, type TemplateDraft } from './role-template-draft';
import { Decision } from './TemplateLibrary';
import { useServerDraft } from './use-server-draft';
import styles from './AdminSetup.module.css';

const TEMPLATE_PROFILE_LEAD = 'The template’s ideal candidate, as the form only. The interview isn’t offered here; build one in a job and save it as a template.';
const TEMPLATE_PROFILE_NOTE = 'A job made from this template gets its own copy of these lines. Editing the template later never changes jobs already made from it.';
const MENU_WIDTH = 260;

type Section = 'basics' | 'description' | 'ideal' | 'criteria' | 'matrix' | 'form' | 'pipeline';

interface TemplateEditorProps {
  /** The template as the server has it now; null for a new one. */
  readonly latest: RoleTemplateView | null;
  readonly canEdit: boolean;
  /** At laptop width and below the queue's Approve and Reject move here, with the list a drop-down. */
  readonly isCompact: boolean;
  readonly onCreated: (id: string) => void;
  readonly onCancelNew: () => void;
  readonly onRefetch: () => void;
}

/**
 * ADMIN-05 (Role templates A): the middle column and the right one. The editor in section tabs — Basics, Job
 * description, Ideal candidate, Screening criteria (only with that part on), Scoring matrix, Application form (only with
 * the career site on) and Pipeline — and beside it what a recruiter gets from it, as it stands now. Every tab stays
 * mounted while another is open, so nothing typed is lost by switching.
 */
export function TemplateEditor({ latest, canEdit, isCompact, onCreated, onCancelNew, onRefetch }: TemplateEditorProps): ReactNode {
  const ventures = useVentures();
  const departments = useDepartments();
  const pipelines = usePipelineTemplates();
  const save = useSaveRoleTemplate();
  const isCareerSiteOn = useModule('career_site');
  const isCriteriaOn = useModule('screening_criteria');
  const show = useToasts((state) => state.show);
  const editor = useServerDraft<RoleTemplateView, TemplateDraft>({
    latest,
    draftOf: templateDraftOf,
    isSameDraft: isSameTemplateDraft,
    isSameEditable: isSameEditableTemplate,
  });
  const { draft, base, isDirty, isStale } = editor;
  const isNew = base === null;
  const headRef = useRef<HTMLElement | null>(null);
  const { guard, release } = useLeaveGuard(canEdit && isDirty, headRef);
  const [section, setSection] = useState<Section>('basics');
  const [isProfileValid, setIsProfileValid] = useState(true);
  // A form that keeps its own lines (the ideal candidate) starts again from the draft after Discard or Reload.
  const [resets, setResets] = useState(0);
  const isReadOnly = !canEdit;

  const change = (patch: Partial<TemplateDraft>): void => editor.setDraft((current) => ({ ...current, ...patch }));
  const isNamed = draft.name.trim().length >= 2 && draft.title.trim().length >= 2;
  // No lines is no ideal candidate, which a template may have; the form itself counts an empty list as unsaveable.
  const isProfileOk = draft.profile.length === 0 || isProfileValid;
  const canSave = canEdit && (isNew || isDirty) && isNamed && isProfileOk && !isStale;

  async function onSave(): Promise<void> {
    if (!canSave || save.isPending) return;
    const sent = draft;
    try {
      const saved = await save.mutateAsync({ id: base?.id ?? null, version: base?.version ?? 0, body: templateBodyOf(sent) });
      show({ tone: 'success', message: `${sent.name.trim()} saved`, detail: 'Jobs already made from it keep their own copy.' });
      if (base === null) {
        release();
        onCreated(saved.id);
        return;
      }
      editor.adopt(saved, sent);
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 409) onRefetch();
      show({ tone: 'danger', message: 'The template was not saved', detail: errorMessage(cause) });
    }
  }

  const cancel = (): void => {
    if (isNew) {
      release();
      onCancelNew();
      return;
    }
    editor.discard();
    setResets((count) => count + 1);
  };
  const reload = (): void => {
    editor.reload();
    setResets((count) => count + 1);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key !== 'Enter' || !(event.ctrlKey || event.metaKey)) return;
    event.preventDefault();
    void onSave();
  };

  const tabs: readonly TabItem<Section>[] = [
    // What stops Save, on the tab it is in — once something has been changed, so nothing flashes while the form loads.
    { value: 'basics', label: 'Basics', ...(isNamed || !isDirty ? {} : { after: 'to fill', afterLabel: 'needs a name and a title' }) },
    { value: 'description', label: 'Job description' },
    { value: 'ideal', label: 'Ideal candidate', ...(isProfileOk || !isDirty ? {} : { after: 'to fix', afterLabel: 'a line needs fixing' }) },
    // ADR 0062 §3: the older way to rank. Off, it leaves no trace here (#13).
    ...(isCriteriaOn ? [{ value: 'criteria' as const, label: 'Screening criteria' }] : []),
    { value: 'matrix', label: 'Scoring matrix' },
    // The application form is the career site's (§L34); off, it leaves no trace here (#13).
    ...(isCareerSiteOn ? [{ value: 'form' as const, label: 'Application form' }] : []),
    { value: 'pipeline', label: 'Pipeline' },
  ];
  const openSection = tabs.some((tab) => tab.value === section) ? section : 'basics';
  const ventureDepartments = (departments.data ?? []).filter((department) => department.ventureId === draft.ventureId);
  const title = isNew ? 'New role template' : (base?.name ?? '');
  // Its status and certification as the server has them now, even while a stale draft waits.
  const current = latest ?? base;

  return (
    <>
      <section className={styles.editor} aria-label={isNew ? 'New role template' : `Role template ${title}`} onKeyDown={onKeyDown}>
        <header ref={headRef} className={styles.editorHead}>
          <h2 className={styles.editorTitle}>{title}</h2>
          {current && current.status !== 'approved' && <ToneChip tone={TEMPLATE_STATUS_TONES[current.status]}>{TEMPLATE_STATUS_LABELS[current.status]}</ToneChip>}
          {current?.isCertified && <Chip tone="success">Certified</Chip>}
          {isDirty && canEdit && (
            <Chip tone="attention" plain>
              Unsaved changes
            </Chip>
          )}
          {canEdit && (
            <span className={styles.editorActions}>
              {current?.status === 'approved' && <CertifyMenu template={current} />}
              {(isDirty || isNew) && (
                <Button variant="ghost" size="sm" onClick={cancel}>
                  Cancel
                </Button>
              )}
              <Button variant="solid" size="sm" disabled={!canSave} isPending={save.isPending} onClick={() => void onSave()}>
                Save template
              </Button>
            </span>
          )}
        </header>
        {canEdit && isCompact && current?.status === 'draft' && <Decision template={current} />}
        {current?.status === 'rejected' && current.decisionReason && (
          <p className={styles.problem}>
            Rejected{current.decidedByName ? ` by ${current.decidedByName}` : ''}: {current.decisionReason}
          </p>
        )}
        {isStale && (
          <div className={styles.stale} role="alert">
            <span>Someone else saved this template while you were editing. Your changes here can't be saved over theirs.</span>
            <Button variant="outline" size="sm" onClick={reload}>
              Reload theirs
            </Button>
          </div>
        )}

        <div className={styles.editorTabs}>
          <Tabs label="Template sections" tabs={tabs} value={openSection} onChange={setSection}>
            <div className={styles.editorBody} hidden={openSection !== 'basics'}>
              <h3 className={styles.sectionTitle}>Basics</h3>
              <div className={styles.fieldRow}>
                <Field label="Template name" isRequired>
                  {({ inputId }) => (
                    <Input id={inputId} disabled={isReadOnly} value={draft.name} maxLength={120} placeholder="Senior accountant" onChange={(event) => change({ name: event.target.value })} />
                  )}
                </Field>
                <Field label="Title candidates see" isRequired>
                  {({ inputId }) => <Input id={inputId} disabled={isReadOnly} value={draft.title} maxLength={160} onChange={(event) => change({ title: event.target.value })} />}
                </Field>
              </div>
              <div className={styles.fieldRow}>
                <Field label="Role family" isOptional hint="Groups jobs for pipelines and reports.">
                  {({ inputId, describedBy }) => (
                    <Input
                      id={inputId}
                      aria-describedby={describedBy}
                      disabled={isReadOnly}
                      value={draft.roleFamily}
                      maxLength={80}
                      placeholder="Finance"
                      onChange={(event) => change({ roleFamily: event.target.value })}
                    />
                  )}
                </Field>
                <Field label="Venture" isOptional>
                  {({ inputId }) => (
                    <Select
                      id={inputId}
                      label="Venture"
                      placeholder="Every venture"
                      disabled={isReadOnly}
                      value={draft.ventureId ?? ''}
                      options={[{ value: '', label: 'Every venture' }, ...(ventures.data ?? []).map((venture) => ({ value: venture.id, label: venture.name }))]}
                      onChange={(value) => change({ ventureId: value === '' ? null : value, departmentId: null })}
                    />
                  )}
                </Field>
                <Field label="Department" isOptional>
                  {({ inputId }) => (
                    <Select
                      id={inputId}
                      label="Department"
                      placeholder="Every department"
                      disabled={isReadOnly || draft.ventureId === null}
                      value={draft.departmentId ?? ''}
                      options={[{ value: '', label: 'Every department' }, ...ventureDepartments.map((department) => ({ value: department.id, label: department.name }))]}
                      onChange={(value) => change({ departmentId: value === '' ? null : value })}
                    />
                  )}
                </Field>
              </div>
              <div className={styles.fieldRow}>
                <Field label="Employment type">
                  {() => (
                    <Segmented
                      label="Employment type"
                      disabled={isReadOnly}
                      value={draft.employmentType}
                      options={EMPLOYMENT_TYPES.map((type) => ({ value: type, label: EMPLOYMENT_TYPE_LABELS[type] }))}
                      onChange={(employmentType: EmploymentType) => change({ employmentType })}
                    />
                  )}
                </Field>
                <Field label="Work mode">
                  {() => (
                    <Segmented
                      label="Work mode"
                      disabled={isReadOnly}
                      value={draft.workMode}
                      options={WORK_MODES.map((mode) => ({ value: mode, label: WORK_MODE_LABELS[mode] }))}
                      onChange={(workMode: WorkMode) => change({ workMode })}
                    />
                  )}
                </Field>
              </div>
              <Checkbox checked={draft.isActive} disabled={isReadOnly} label="Recruiters can pick this template" onChange={(isActive) => change({ isActive })} />
            </div>

            <div className={styles.editorBody} hidden={openSection !== 'description'}>
              <h3 className={styles.sectionTitle}>Job description</h3>
              <Field label="About the job">
                {({ inputId }) => <Textarea id={inputId} rows={4} disabled={isReadOnly} value={draft.description} onChange={(event) => change({ description: event.target.value })} />}
              </Field>
              <Field label="Responsibilities" isOptional>
                {({ inputId }) => (
                  <Textarea id={inputId} rows={3} disabled={isReadOnly} value={draft.responsibilities} onChange={(event) => change({ responsibilities: event.target.value })} />
                )}
              </Field>
              <Field label="Requirements" isOptional>
                {({ inputId }) => <Textarea id={inputId} rows={3} disabled={isReadOnly} value={draft.requirements} onChange={(event) => change({ requirements: event.target.value })} />}
              </Field>
            </div>

            <div className={styles.editorBody} hidden={openSection !== 'ideal'}>
              <h3 className={styles.sectionTitle}>Ideal candidate</h3>
              <p className={styles.note}>{TEMPLATE_PROFILE_LEAD}</p>
              <IcpInlineForm
                key={`${base?.version ?? 0}:${resets}`}
                defaultItems={draft.profile.map(itemOfInput)}
                notes={[]}
                isRemote={draft.workMode === 'remote'}
                budget={{ kind: 'none' }}
                onChange={(items) => change({ profile: items.map(itemInputOf) })}
                onValidChange={setIsProfileValid}
                footer={() => <span>{TEMPLATE_PROFILE_NOTE}</span>}
              />
            </div>

            {isCriteriaOn && (
              <div className={styles.editorBody} hidden={openSection !== 'criteria'}>
                <h3 className={styles.sectionTitle}>Screening criteria</h3>
                <p className={styles.note}>Weights are worked out from importance.</p>
                <CriteriaTable rows={draft.criteria} isReadOnly={isReadOnly} onRowsChange={(criteria) => change({ criteria })} onCommit={async (criteria) => change({ criteria })} />
              </div>
            )}

            <div className={styles.editorBody} hidden={openSection !== 'matrix'}>
              <h3 className={styles.sectionTitle}>Scoring matrix</h3>
              <p className={styles.note}>What interviewers score, 1 to 5.</p>
              <MatrixTable
                rows={draft.matrix}
                roundCount={draft.interviewRounds.length}
                isReadOnly={isReadOnly}
                onRowsChange={(matrix) => change({ matrix })}
                onCommit={async (matrix) => change({ matrix })}
                noRoundsNote="This template has no interview rounds, so only HR review can be ticked."
              />
            </div>

            {isCareerSiteOn && (
              <div className={styles.editorBody} hidden={openSection !== 'form'}>
                <h3 className={styles.sectionTitle}>Application form</h3>
                <FormBuilder
                  formFields={draft.formFields}
                  questions={draft.questions}
                  criteriaTypes={draft.criteria.map((criterion) => draft.legacy.get(criterion.id)?.type ?? legacyTypeFromKind(criterion.kind))}
                  isReadOnly={isReadOnly}
                  onChange={(next) => change(next)}
                />
              </div>
            )}

            <div className={styles.editorBody} hidden={openSection !== 'pipeline'}>
              <div className={styles.sectionHead}>
                <h3 className={styles.sectionTitle}>Pipeline</h3>
                {canEdit && (pipelines.data ?? []).length > 0 && (
                  <Select
                    label="Start from a pipeline"
                    placeholder="Start from a pipeline…"
                    value={null}
                    options={(pipelines.data ?? []).map((pipeline) => ({ value: pipeline.id, label: pipeline.name, description: pipeline.roleFamily ?? 'Any role family' }))}
                    onChange={(id) => {
                      const source = pipelines.data?.find((pipeline) => pipeline.id === id);
                      if (source) change({ pipeline: source.pipeline });
                    }}
                  />
                )}
              </div>
              <p className={styles.note}>A job made from this template copies these stages. Editing a pipeline later never changes it.</p>
              {/* R9 (QoL §7 rule 4): stage names are set once and rarely touched. */}
              <MoreOptions label="Stage names, and what candidates see">
                <StageBuilder value={draft.pipeline} isReadOnly={isReadOnly} onChange={(pipeline) => change({ pipeline })} />
              </MoreOptions>
            </div>
          </Tabs>
        </div>
        {guard}
      </section>
      <RecruiterView draft={draft} template={current} />
    </>
  );
}

/**
 * F5.23 (O27): certify, or take it back, in the ⋯ menu beside its button. It just happens, with a 10-second Undo
 * (CLAUDE.md §5a rule 8); only an approved template can be certified.
 */
function CertifyMenu({ template }: { readonly template: RoleTemplateView }): ReactNode {
  const certify = useCertifyRoleTemplate();
  const show = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  const { anchor, triggerRef, isOpen, toggle, close } = usePopover();

  async function setCertified(isCertified: boolean): Promise<void> {
    close();
    try {
      await certify.mutateAsync({ id: template.id, isCertified });
      showUndo(isCertified ? `${template.name} certified` : `${template.name} is no longer certified`, () => {
        certify
          .mutateAsync({ id: template.id, isCertified: !isCertified })
          .catch((cause: unknown) => show({ tone: 'danger', message: 'That was not undone', detail: errorMessage(cause) }));
      });
    } catch (cause) {
      show({ tone: 'danger', message: 'Nothing was changed', detail: errorMessage(cause) });
    }
  }

  return (
    <>
      <Button
        ref={triggerRef}
        variant="ghost"
        size="sm"
        icon={MoreHorizontal}
        aria-label="More for this template"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        isPending={certify.isPending}
        onClick={toggle}
      />
      <Popover anchor={anchor} onClose={close} align="right" width={MENU_WIDTH} label="More for this template">
        <MenuItem icon={<BadgeCheck size={16} aria-hidden />} onClick={() => void setCertified(!template.isCertified)}>
          {template.isCertified ? 'Remove certification' : 'Certify'}
        </MenuItem>
      </Popover>
    </>
  );
}
